/** @format */

import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { produce } from "immer";
import { api } from "../api/client";
import { SOP, type SOPVersion, type StepEditPayload } from "../types/sop";
import { useSopStore } from "./useSopStore";
import { useSopSelectionStore } from "./useSopSelectionStore";
import {
  isVersionConflict,
  isVersionLockedError,
  getVersionLockedMessage,
  VERSION_CONFLICT_MESSAGE,
} from "../api/sopErrors";

interface SopEditActions {
  editStep: (payload: StepEditPayload) => Promise<void>;
  clearError: () => void;
}

interface SopEditState extends SopEditActions {
  isUpdating: boolean;
  error: string | null;
}

export const useSopEditStore = create<SopEditState>()(
  devtools(
    subscribeWithSelector((set) => ({
      isUpdating: false,
      error: null,

      clearError: () => set({ error: null }),

      /**
       * Edit a step in the latest version of the selected SOP and update the backend
       * @param stepIndex - The index of the step to edit
       * @param newTitle - The new title for the step
       * @param newDetails - The new details for the step
       * @param newTools - The new tools for the step
       * @param newParts - The new parts for the step
       * @param newImageReferences - The new image references for the step
       * @returns void
       */
      editStep: async ({
        stepIndex,
        newTitle,
        newDetails,
        newTools,
        newParts,
        newImageReferences,
      }: StepEditPayload) => {
        const { selectedSOP } = useSopSelectionStore.getState();
        const sop = useSopStore.getState().getSopById(selectedSOP);
        if (!sop) return;

        const latestVersion = sop.getLatestVersion();
        if (!latestVersion) return;

        // Store original state for rollback
        const originalSteps = [...latestVersion.steps];

        try {
          set({ isUpdating: true, error: null });

          // Optimistic update
          useSopStore.setState(
            produce((state) => {
              const sop = state.sops.find((s: SOP) => s.sopId === selectedSOP);
              if (!sop) return;

              const latestVersion = sop.getLatestVersion();
              if (!latestVersion) return;

              if (latestVersion.steps[stepIndex]) {
                const updatedStep = {
                  ...latestVersion.steps[stepIndex],
                  title: newTitle,
                  details: newDetails,
                  tools: newTools ?? latestVersion.steps[stepIndex].tools,
                  parts: newParts ?? latestVersion.steps[stepIndex].parts,
                  imageReferences:
                    newImageReferences ??
                    latestVersion.steps[stepIndex].imageReferences,
                };

                latestVersion.steps = [
                  ...latestVersion.steps.slice(0, stepIndex),
                  updatedStep,
                  ...latestVersion.steps.slice(stepIndex + 1),
                ];
              }
            }),
          );

          // Backend sync – PUT /sops/<sop_id>/steps/<step_index> (backend resolves latest version)
          const latestVersion = useSopStore
            .getState()
            .getLatestVersion(selectedSOP);
          const res = await api.put(
            `/sops/${sop.sopId}/steps/${stepIndex}`,
            {
              title: newTitle,
              details: newDetails,
              image: newImageReferences,
            },
          );

          const data = res.data;
          const newVersion: SOPVersion = {
            versionId: data.version_id,
            versionNum: data.version_num,
            versionTitle:
              data.version_title && data.version_title?.trim() !== ""
                ? data.version_title
                : `Version ${data.version_num}`,
            steps: latestVersion?.steps || [],
            titlePage: latestVersion?.titlePage, // Preserve title page from previous version
            editedAt: new Date().toISOString(),
          };

          useSopStore.getState().addVersionToSOP(sop.sopId, newVersion);

          // Update selection store to reflect new version
          useSopSelectionStore.setState({
            selectedVersion: newVersion,
            latestVersion: newVersion,
          });

          set({ isUpdating: false });
        } catch (error) {
          if (isVersionLockedError(error)) {
            set({
              error: getVersionLockedMessage(error),
              isUpdating: false,
            });
            useSopStore.setState(
              produce((state) => {
                const s = state.sops.find((sop: SOP) => sop.sopId === selectedSOP);
                if (s) {
                  const latest = s.getLatestVersion();
                  if (latest) latest.steps = originalSteps;
                }
              }),
            );
            return;
          }
          if (isVersionConflict(error)) {
            // Auto-recover: refresh latest versionId and retry once with the user's edit.
            try {
              await useSopStore.getState().fetchSOPs();
              useSopSelectionStore.getState().refreshSelectedSOP();
              const latestVersionRetry = useSopStore
                .getState()
                .getLatestVersion(selectedSOP);
              if (!latestVersionRetry) {
                set({ error: VERSION_CONFLICT_MESSAGE, isUpdating: false });
                return;
              }

              // Re-apply user's edit to latest in-memory state (so UI keeps the change)
              useSopStore.setState(
                produce((state) => {
                  const sop = state.sops.find(
                    (s: SOP) => s.sopId === selectedSOP,
                  );
                  const latest = sop?.getLatestVersion();
                  const target = latest?.steps?.[stepIndex];
                  if (!sop || !latest || !target) return;
                  latest.steps[stepIndex] = {
                    ...target,
                    title: newTitle,
                    details: newDetails,
                    tools: newTools ?? target.tools,
                    parts: newParts ?? target.parts,
                    imageReferences:
                      newImageReferences ?? target.imageReferences,
                  };
                }),
              );

              const sopRetry = useSopStore.getState().getSopById(selectedSOP);
              const latestForPut = sopRetry?.getLatestVersion();
              const res2 = await api.put(
                `/sops/${sopRetry?.sopId}/steps/${stepIndex}`,
                {
                  title: newTitle,
                  details: newDetails,
                  image: newImageReferences,
                },
              );

              const data2 = res2.data;
              const newVersion2: SOPVersion = {
                versionId: data2.version_id,
                versionNum: data2.version_num,
                versionTitle:
                  data2.version_title && data2.version_title?.trim() !== ""
                    ? data2.version_title
                    : `Version ${data2.version_num}`,
                steps: latestForPut?.steps || [],
                titlePage: latestForPut?.titlePage,
                editedAt: new Date().toISOString(),
              };

              useSopStore
                .getState()
                .addVersionToSOP(sopRetry!.sopId, newVersion2);
              useSopSelectionStore.setState({
                selectedVersion: newVersion2,
                latestVersion: newVersion2,
              });
              set({ isUpdating: false });
              return;
            } catch {
              set({ error: VERSION_CONFLICT_MESSAGE, isUpdating: false });
              return;
            }
          }

          // Rollback optimistic update (only for non-conflict failures)
          useSopStore.setState(
            produce((state) => {
              const sop = state.sops.find((s: SOP) => s.sopId === selectedSOP);
              if (sop) {
                const latestVersion = sop.getLatestVersion();
                if (latestVersion) {
                  latestVersion.steps = originalSteps;
                }
              }
            }),
          );

          set({
            error:
              error instanceof Error ? error.message : "Failed to update step",
            isUpdating: false,
          });
        }
      },
    })),
    {
      name: "sop-edit-store",
      enabled: process.env.NODE_ENV === "development",
    },
  ),
);
